import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface LessonPlannerFormProps {
  onGenerate: (config: {
    totalLessons: number;
    frequency: string;
    startDate: string;
    notes: string;
  }) => void;
  isGenerating?: boolean;
  disabled?: boolean;
}

export default function LessonPlannerForm({
  onGenerate,
  isGenerating = false,
  disabled = false,
}: LessonPlannerFormProps) {
  const [totalLessons, setTotalLessons] = useState<number>(8);
  const [frequency, setFrequency] = useState<string>("2x");
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>("");

  const handleSubmit = () => {
    onGenerate({
      totalLessons,
      frequency,
      startDate: format(startDate, "yyyy-MM-dd"),
      notes,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Quantidade de aulas */}
        <div className="space-y-2">
          <Label htmlFor="total-lessons">Quantidade de aulas</Label>
          <Select
            value={totalLessons.toString()}
            onValueChange={(value) => setTotalLessons(parseInt(value))}
            disabled={disabled || isGenerating}
          >
            <SelectTrigger id="total-lessons">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="4">4 aulas</SelectItem>
              <SelectItem value="8">8 aulas</SelectItem>
              <SelectItem value="12">12 aulas</SelectItem>
              <SelectItem value="16">16 aulas</SelectItem>
              <SelectItem value="24">24 aulas</SelectItem>
              <SelectItem value="32">32 aulas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Frequência semanal */}
        <div className="space-y-2">
          <Label htmlFor="frequency">Frequência semanal</Label>
          <Select
            value={frequency}
            onValueChange={setFrequency}
            disabled={disabled || isGenerating}
          >
            <SelectTrigger id="frequency">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1x">1x por semana</SelectItem>
              <SelectItem value="2x">2x por semana</SelectItem>
              <SelectItem value="3x">3x por semana</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data de início */}
      <div className="space-y-2">
        <Label>Data de início</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
              disabled={disabled || isGenerating}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? (
                format(startDate, "PPP", { locale: ptBR })
              ) : (
                <span>Selecione uma data</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={(date) => date && setStartDate(date)}
              initialFocus
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label htmlFor="notes">Observações adicionais (opcional)</Label>
        <Textarea
          id="notes"
          placeholder="Ex: Foco em exercícios de baixo impacto, evitar movimentos bruscos..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={disabled || isGenerating}
          rows={3}
          className="resize-none"
        />
      </div>

      {/* Botão Gerar */}
      <Button
        onClick={handleSubmit}
        disabled={disabled || isGenerating}
        size="lg"
        className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
      >
        <Sparkles className="mr-2 h-5 w-5" />
        {isGenerating ? "Gerando plano..." : "Gerar Plano com IA"}
      </Button>
    </div>
  );
}
